import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings, GripVertical, Plus, X as XIcon, Save, FileText, FileSpreadsheet,
  Loader2, Trash2, Copy, Filter, Columns3, Layers, PenLine, Eye, CheckCircle2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { reportesApi, receptoresApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import ConfirmModal from '../../components/ConfirmModal';

// Catálogo de columnas (las keys deben coincidir con el backend)
const CATALOGO = [
  { key: 'consecutivo', label: 'Consecutivo', grupo: 'Identificación', money: false },
  { key: 'clave',       label: 'Clave',       grupo: 'Identificación', money: false },
  { key: 'fecha',       label: 'Fecha',       grupo: 'Identificación', money: false },
  { key: 'cliente',     label: 'Cliente',     grupo: 'Cliente',        money: false },
  { key: 'cedula',      label: 'Cédula',      grupo: 'Cliente',        money: false },
  { key: 'tipo_doc',    label: 'Tipo doc.',   grupo: 'Clasificación',  money: false },
  { key: 'estado',      label: 'Estado',      grupo: 'Clasificación',  money: false },
  { key: 'moneda',      label: 'Moneda',      grupo: 'Clasificación',  money: false },
  { key: 'tipo_cambio', label: 'Tipo de cambio', grupo: 'Clasificación', money: false },
  { key: 'condicion',   label: 'Condición',   grupo: 'Clasificación',  money: false },
  { key: 'gravado',     label: 'Gravado',     grupo: 'Montos (CRC)',   money: true },
  { key: 'exento',      label: 'Exento',      grupo: 'Montos (CRC)',   money: true },
  { key: 'exonerado',   label: 'Exonerado',   grupo: 'Montos (CRC)',   money: true },
  { key: 'descuento',   label: 'Descuento',   grupo: 'Montos (CRC)',   money: true },
  { key: 'valor',       label: 'Valor sin IVA', grupo: 'Montos (CRC)', money: true },
  { key: 'iva',         label: 'IVA',         grupo: 'Montos (CRC)',   money: true },
  { key: 'total',       label: 'Total',       grupo: 'Montos (CRC)',   money: true },
];
const LABELS = Object.fromEntries(CATALOGO.map(c => [c.key, c.label]));
const IS_MONEY = Object.fromEntries(CATALOGO.map(c => [c.key, c.money]));

const TIPOS_DOC = [
  { v: '01', l: 'Factura (FE)' },
  { v: '02', l: 'Nota Débito (ND)' },
  { v: '03', l: 'Nota Crédito (NC)' },
  { v: '04', l: 'Tiquete (TE)' },
];

const iso = (d) => d.toISOString().split('T')[0];
const hoy = new Date();
const primerDiaMes = (d) => iso(new Date(d.getFullYear(), d.getMonth(), 1));
const ultimoDiaMes = (d) => iso(new Date(d.getFullYear(), d.getMonth() + 1, 0));

// Config por defecto = Reporte de Facturación (Banco)
const CONFIG_DEFAULT = () => ({
  titulo: 'Resumen de Ventas',
  desde: primerDiaMes(new Date(hoy.getFullYear(), hoy.getMonth() - 2, 1)),
  hasta: ultimoDiaMes(hoy),
  tiposDoc: ['01', '02', '03', '04'],
  estado: 'aceptado',
  moneda: 'todas',
  condicion: 'todas',
  receptorId: '',
  columnas: ['consecutivo', 'fecha', 'cliente', 'moneda', 'valor', 'iva', 'total'],
  agrupacion: 'mes',
  ncNegativo: true,
  mostrarTotales: true,
  portada: true,
  mostrarFirmas: true,
  representanteLegal: '',
  contadorNombre: '',
  contadorCpi: '',
  contadorColegiado: '',
});

const PLANTILLA_BANCO = {
  id: 'banco',
  nombre: 'Reporte de Facturación (Banco)',
  fija: true,
  config: CONFIG_DEFAULT(),
};

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const fmtNum = (n) => (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function ReportesPersonalizados() {
  const { currentEmisor } = useAuthStore();
  const [config, setConfig] = useState(CONFIG_DEFAULT());
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaId, setPlantillaId] = useState('banco');

  const [receptores, setReceptores] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [actualizado, setActualizado] = useState(false);
  const actualizadoTimer = useRef(null);

  const [genLoading, setGenLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [showFull, setShowFull] = useState(false);

  const [nameModal, setNameModal] = useState({ open: false, value: '' });
  const [confirmDel, setConfirmDel] = useState(false);

  const key = `reportes_personalizados_${currentEmisor?.id || 'x'}`;

  // Cargar plantillas guardadas + receptores
  useEffect(() => {
    // Precargar los firmantes que ya se usaron en el Reporte de Facturación (Banco)
    let banco = PLANTILLA_BANCO;
    try {
      const perEmisor = JSON.parse(localStorage.getItem(`reporte_ventas_firmantes_${currentEmisor?.id || 'x'}`) || '{}');
      const global = JSON.parse(localStorage.getItem('reporte_ventas_firmantes_global') || '{}');
      const s = (perEmisor && Object.keys(perEmisor).length) ? perEmisor : global;
      if (s && Object.keys(s).length) {
        const cfg = {
          ...CONFIG_DEFAULT(),
          ...(s.titulo ? { titulo: s.titulo } : {}),
          representanteLegal: s.rep || '',
          contadorNombre: s.contNombre || '',
          contadorCpi: s.contCpi || '',
          contadorColegiado: s.contColeg || '',
        };
        banco = { ...PLANTILLA_BANCO, config: cfg };
        if (plantillaId === 'banco') setConfig(cfg);
      }
    } catch { /* sin firmantes previos */ }
    try {
      const saved = JSON.parse(localStorage.getItem(key) || '[]');
      setPlantillas([banco, ...saved.filter(p => p.id !== 'banco')]);
    } catch { setPlantillas([banco]); }
    receptoresApi.list({ limit: 1000 }).then(res => {
      const list = res.data?.data || res.data?.receptores || res.data || [];
      setReceptores(Array.isArray(list) ? list : []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const set = (patch) => setConfig(c => ({ ...c, ...patch }));

  // Vista previa en vivo (debounced) con guard de secuencia:
  // solo se aplica la respuesta de la ÚLTIMA consulta disparada (evita respuestas fuera de orden)
  const reqIdRef = useRef(0);
  const cargarPreview = useCallback(async (cfg) => {
    if (!cfg.desde || !cfg.hasta) { setPreview(null); return; }
    const myId = ++reqIdRef.current;
    setPreviewLoading(true);
    try {
      const res = await reportesApi.reportePersonalizadoPreview(cfg);
      if (myId !== reqIdRef.current) return; // llegó tarde: ya hay una consulta más nueva
      setPreview(res.data?.data || null);
      // Mostrar "Actualizado" por un momento
      setActualizado(true);
      if (actualizadoTimer.current) clearTimeout(actualizadoTimer.current);
      actualizadoTimer.current = setTimeout(() => setActualizado(false), 1800);
    } catch (e) {
      if (myId !== reqIdRef.current) return;
      console.error(e);
      setPreview(null);
    } finally {
      if (myId === reqIdRef.current) setPreviewLoading(false);
    }
  }, []);

  const debRef = useRef(null);
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => cargarPreview(config), 500);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [config, cargarPreview]);

  // ---- Plantillas ----
  const guardarPlantillas = (arr) => {
    const guardables = arr.filter(p => !p.fija);
    localStorage.setItem(key, JSON.stringify(guardables));
    setPlantillas([PLANTILLA_BANCO, ...guardables]);
  };
  const cargarPlantilla = (id) => {
    const p = plantillas.find(x => x.id === id);
    setPlantillaId(id);
    if (p) setConfig({ ...CONFIG_DEFAULT(), ...p.config });
  };
  const abrirGuardarComo = () => setNameModal({ open: true, value: '' });
  const confirmarGuardarComo = () => {
    const nombre = (nameModal.value || '').trim();
    if (!nombre) { toast.error('Escriba un nombre'); return; }
    const id = `t_${nombre.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_${hoy.getFullYear()}`;
    const nueva = { id, nombre, config: { ...config } };
    const otras = plantillas.filter(p => p.id !== id && !p.fija);
    guardarPlantillas([...otras, nueva]);
    setPlantillaId(id);
    setNameModal({ open: false, value: '' });
    toast.success('Plantilla guardada');
  };
  const actualizarPlantilla = () => {
    const p = plantillas.find(x => x.id === plantillaId);
    if (!p || p.fija) { abrirGuardarComo(); return; }
    const otras = plantillas.filter(x => x.id !== plantillaId && !x.fija);
    guardarPlantillas([...otras, { ...p, config: { ...config } }]);
    toast.success('Plantilla actualizada');
  };
  const eliminarPlantilla = () => {
    const p = plantillas.find(x => x.id === plantillaId);
    if (!p || p.fija) return;
    guardarPlantillas(plantillas.filter(x => x.id !== plantillaId && !x.fija));
    setPlantillaId('banco');
    setConfig(CONFIG_DEFAULT());
    toast.success('Plantilla eliminada');
  };

  // ---- Columnas (drag & drop) ----
  const dragIdx = useRef(null);
  const onDrop = (i) => {
    const from = dragIdx.current;
    if (from == null || from === i) return;
    const arr = [...config.columnas];
    const [m] = arr.splice(from, 1);
    arr.splice(i, 0, m);
    set({ columnas: arr });
    dragIdx.current = null;
  };
  const toggleCol = (k) => {
    set({ columnas: config.columnas.includes(k) ? config.columnas.filter(x => x !== k) : [...config.columnas, k] });
  };
  const quitarCol = (k) => set({ columnas: config.columnas.filter(x => x !== k) });

  const toggleTipo = (v) => {
    set({ tiposDoc: config.tiposDoc.includes(v) ? config.tiposDoc.filter(x => x !== v) : [...config.tiposDoc, v] });
  };

  // ---- Generar / Guardar ----
  const nombreArchivo = (ext) => {
    const d = new Date(config.hasta + 'T00:00:00');
    const slug = (currentEmisor?.nombre || 'Emisor').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    return `${d.getFullYear()}-${MESES[d.getMonth()]}-${slug}.${ext}`;
  };
  const guardarBlob = async (blob, filename, accept) => {
    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({ suggestedName: filename, types: [{ description: filename.split('.').pop().toUpperCase(), accept }] });
        const w = await handle.createWritable();
        await w.write(blob); await w.close();
        toast.success('Guardado'); return;
      } catch (e) { if (e && e.name === 'AbortError') return; }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Recordar firmantes para la próxima vez (compartido con el Reporte de Facturación)
  const persistFirmantes = () => {
    const f = { rep: config.representanteLegal, contNombre: config.contadorNombre, contCpi: config.contadorCpi, contColeg: config.contadorColegiado, titulo: config.titulo };
    localStorage.setItem(`reporte_ventas_firmantes_${currentEmisor?.id || 'x'}`, JSON.stringify(f));
    localStorage.setItem('reporte_ventas_firmantes_global', JSON.stringify(f));
  };

  const generarPdf = async () => {
    if (!config.columnas.length) { toast.error('Seleccione al menos una columna'); return; }
    persistFirmantes();
    setGenLoading(true);
    try {
      const res = await reportesApi.reportePersonalizadoPdf(config);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfBlob(blob); setPdfUrl(url); setShowFull(true);
    } catch (e) {
      console.error(e); toast.error('Error al generar el PDF');
    } finally { setGenLoading(false); }
  };
  const guardarPdf = async () => {
    if (!pdfBlob) return;
    await guardarBlob(pdfBlob, nombreArchivo('pdf'), { 'application/pdf': ['.pdf'] });
  };
  const guardarExcel = async () => {
    if (!config.columnas.length) { toast.error('Seleccione al menos una columna'); return; }
    persistFirmantes();
    setExcelLoading(true);
    try {
      const res = await reportesApi.reportePersonalizadoExcel(config);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      await guardarBlob(blob, nombreArchivo('xlsx'), { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] });
    } catch (e) {
      console.error(e); toast.error('Error al generar el Excel');
    } finally { setExcelLoading(false); }
  };

  const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500';
  const lbl = 'block text-xs font-medium text-gray-600 mb-1';
  const seleccionadas = new Set(config.columnas);

  return (
    <div className="space-y-4">
      {/* Barra de plantillas */}
      <div className="bg-white rounded-xl border p-3 flex flex-wrap items-center justify-center gap-2">
        <span className="text-xs font-medium text-gray-500 flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> Plantilla:</span>
        <select value={plantillaId} onChange={(e) => cargarPlantilla(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
          {plantillas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
        </select>
        <button onClick={actualizarPlantilla} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold border-2 border-gray-300 rounded-lg hover:bg-gray-50">
          <Save className="h-3.5 w-3.5" /> Guardar
        </button>
        <button onClick={abrirGuardarComo} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold border-2 border-gray-300 rounded-lg hover:bg-gray-50">
          <Copy className="h-3.5 w-3.5" /> Guardar como…
        </button>
        {plantillaId !== 'banco' && (
          <button onClick={() => setConfirmDel(true)} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold border-2 border-red-200 text-red-600 rounded-lg hover:bg-red-50">
            <Trash2 className="h-3.5 w-3.5" /> Eliminar
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        {/* Panel de configuración */}
        <div className="xl:col-span-2 space-y-4">
          {/* Datos generales */}
          <div className="bg-white rounded-xl border p-4 space-y-3">
            <div>
              <label className={lbl}>Título del reporte</label>
              <input type="text" value={config.titulo} onChange={(e) => set({ titulo: e.target.value })} placeholder="Ej: Resumen de Ventas" className={input} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Desde</label><input type="date" value={config.desde} onChange={(e) => set({ desde: e.target.value })} className={input} /></div>
              <div><label className={lbl}>Hasta</label><input type="date" value={config.hasta} onChange={(e) => set({ hasta: e.target.value })} className={input} /></div>
            </div>
          </div>

          {/* Filtros */}
          <div className="bg-white rounded-xl border p-4 space-y-3">
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5"><Filter className="h-4 w-4 text-blue-500" /> Filtros</p>
            <div>
              <label className={lbl}>Tipos de documento</label>
              <div className="flex flex-wrap gap-1.5">
                {TIPOS_DOC.map(t => (
                  <button key={t.v} onClick={() => toggleTipo(t.v)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border ${config.tiposDoc.includes(t.v) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                    {t.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Estado Hacienda</label>
                <select value={config.estado} onChange={(e) => set({ estado: e.target.value })} className={input}>
                  <option value="aceptado">Solo aceptados</option>
                  <option value="todos">Todos</option>
                </select>
              </div>
              <div>
                <label className={lbl}>Moneda</label>
                <select value={config.moneda} onChange={(e) => set({ moneda: e.target.value })} className={input}>
                  <option value="todas">Todas</option>
                  <option value="CRC">Colones</option>
                  <option value="USD">Dólares</option>
                </select>
              </div>
              <div>
                <label className={lbl}>Condición</label>
                <select value={config.condicion} onChange={(e) => set({ condicion: e.target.value })} className={input}>
                  <option value="todas">Todas</option>
                  <option value="01">Contado</option>
                  <option value="02">Crédito</option>
                </select>
              </div>
              <div>
                <label className={lbl}>Cliente (opcional)</label>
                <select value={config.receptorId} onChange={(e) => set({ receptorId: e.target.value })} className={input}>
                  <option value="">Todos</option>
                  {receptores.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Columnas */}
          <div className="bg-white rounded-xl border p-4 space-y-3">
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5"><Columns3 className="h-4 w-4 text-purple-500" /> Columnas <span className="text-xs font-normal text-gray-400">(arrastrá para ordenar)</span></p>
            <div className="space-y-1.5">
              {config.columnas.length === 0 && <p className="text-xs text-gray-400 italic">Sin columnas. Agregá abajo.</p>}
              {config.columnas.map((k, i) => (
                <div key={k} draggable
                  onDragStart={() => (dragIdx.current = i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(i)}
                  className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg cursor-move group">
                  <GripVertical className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-700 flex-1">{LABELS[k]}</span>
                  {IS_MONEY[k] && <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">₡ suma</span>}
                  <button onClick={() => quitarCol(k)} className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100"><XIcon className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
            {/* Agregar columnas */}
            <details className="border-t pt-2">
              <summary className="text-xs font-medium text-blue-600 cursor-pointer flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Agregar / quitar columnas</summary>
              <div className="mt-2 space-y-2">
                {['Identificación', 'Cliente', 'Clasificación', 'Montos (CRC)'].map(grupo => (
                  <div key={grupo}>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase mb-1">{grupo}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {CATALOGO.filter(c => c.grupo === grupo).map(c => (
                        <button key={c.key} onClick={() => toggleCol(c.key)}
                          className={`px-2 py-0.5 rounded text-xs border ${seleccionadas.has(c.key) ? 'bg-purple-100 text-purple-700 border-purple-300' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}>
                          {seleccionadas.has(c.key) ? '✓ ' : '+ '}{c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          </div>

          {/* Agrupación y opciones */}
          <div className="bg-white rounded-xl border p-4 space-y-3">
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5"><Layers className="h-4 w-4 text-amber-500" /> Agrupación y totales</p>
            <div>
              <label className={lbl}>Agrupar por</label>
              <select value={config.agrupacion} onChange={(e) => set({ agrupacion: e.target.value })} className={input}>
                <option value="none">Sin agrupar</option>
                <option value="mes">Mes</option>
                <option value="cliente">Cliente</option>
                <option value="tipo">Tipo de documento</option>
                <option value="moneda">Moneda</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={config.mostrarTotales} onChange={(e) => set({ mostrarTotales: e.target.checked })} className="rounded" />
              Mostrar totales (por grupo y total general)
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={config.ncNegativo} onChange={(e) => set({ ncNegativo: e.target.checked })} className="rounded" />
              Notas de Crédito en negativo (restan del total)
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={config.portada} onChange={(e) => set({ portada: e.target.checked })} className="rounded" />
              Portada en página aparte (encabezado en la primera página)
            </label>
          </div>

          {/* Firmas */}
          <div className="bg-white rounded-xl border p-4 space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <input type="checkbox" checked={config.mostrarFirmas} onChange={(e) => set({ mostrarFirmas: e.target.checked })} className="rounded" />
              <PenLine className="h-4 w-4 text-gray-500" /> Bloque de firmas (para banco)
            </label>
            {config.mostrarFirmas && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className={lbl}>Representante Legal</label><input type="text" value={config.representanteLegal} onChange={(e) => set({ representanteLegal: e.target.value })} placeholder="Nombre del representante legal" className={input} /></div>
                <div><label className={lbl}>Contador (nombre)</label><input type="text" value={config.contadorNombre} onChange={(e) => set({ contadorNombre: e.target.value })} placeholder="Nombre del contador" className={input} /></div>
                <div><label className={lbl}>Contador — Cpi</label><input type="text" value={config.contadorCpi} onChange={(e) => set({ contadorCpi: e.target.value })} placeholder="N.º de Cpi" className={input} /></div>
                <div><label className={lbl}>Contador — N° Colegiado</label><input type="text" value={config.contadorColegiado} onChange={(e) => set({ contadorColegiado: e.target.value })} placeholder="N.º de colegiado" className={input} /></div>
              </div>
            )}
          </div>
        </div>

        {/* Vista previa en vivo */}
        <div className="xl:col-span-3">
          <div className="bg-white rounded-xl border overflow-hidden sticky top-4">
            <div className="px-4 py-2.5 border-b flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Eye className="h-4 w-4 text-gray-400" /> Vista previa
                {previewLoading && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Actualizando…
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={generarPdf} disabled={genLoading} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-50">
                  {genLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} PDF
                </button>
                <button onClick={guardarExcel} disabled={excelLoading} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-700 text-white rounded-lg text-xs font-semibold hover:bg-green-800 disabled:opacity-50">
                  {excelLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />} Excel
                </button>
              </div>
            </div>
            <div className="relative overflow-auto" style={{ maxHeight: '70vh' }}>
              <PreviewTabla preview={preview} config={config} />
            </div>

            {/* Cápsula emergente estilo Apple */}
            <div className="pointer-events-none absolute inset-x-0 top-14 flex justify-center z-20">
              <AnimatePresence>
                {actualizado && (
                  <motion.div
                    key="actualizado"
                    initial={{ opacity: 0, scale: 0.6, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: -8 }}
                    transition={{ type: 'spring', stiffness: 520, damping: 24, mass: 0.7 }}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur-xl border border-white/60 shadow-[0_8px_30px_rgba(0,0,0,0.12)]"
                  >
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.08, type: 'spring', stiffness: 600, damping: 18 }}
                      className="flex items-center justify-center h-5 w-5 rounded-full bg-emerald-500"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                    </motion.span>
                    <span className="text-sm font-semibold text-gray-800 tracking-tight">Actualizado</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Modal PDF a pantalla completa */}
      {showFull && pdfUrl && createPortal(
        <div className="fixed inset-0 z-[100] bg-gray-900/80 flex flex-col">
          <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b shrink-0">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800"><FileText className="h-4 w-4 text-blue-600" /> {config.titulo}</div>
            <div className="flex items-center gap-2">
              <button onClick={guardarPdf} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700"><Save className="h-4 w-4" /> Guardar PDF</button>
              <button onClick={guardarExcel} disabled={excelLoading} className="inline-flex items-center gap-2 px-4 py-2 bg-green-700 text-white rounded-lg text-sm font-semibold hover:bg-green-800 disabled:opacity-50">
                {excelLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Guardar Excel
              </button>
              <button onClick={() => setShowFull(false)} className="inline-flex items-center gap-2 px-3 py-2 border-2 border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50"><XIcon className="h-4 w-4" /> Cerrar</button>
            </div>
          </div>
          <iframe src={pdfUrl} title="Reporte" className="flex-1 w-full bg-gray-200" />
        </div>,
        document.body
      )}

      {/* Modal: nombre de la plantilla */}
      {nameModal.open && createPortal(
        <div className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-4" onClick={() => setNameModal({ open: false, value: '' })}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Guardar plantilla</h3>
            <input autoFocus type="text" value={nameModal.value}
              onChange={(e) => setNameModal(m => ({ ...m, value: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && confirmarGuardarComo()}
              placeholder="Nombre de la plantilla" className={input} />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setNameModal({ open: false, value: '' })} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button onClick={confirmarGuardarComo} className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700">Guardar</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ConfirmModal
        isOpen={confirmDel}
        onClose={() => setConfirmDel(false)}
        onConfirm={eliminarPlantilla}
        title="Eliminar plantilla"
        message={`¿Eliminar la plantilla "${plantillas.find(p => p.id === plantillaId)?.nombre || ''}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
      />
    </div>
  );
}

// Tabla de vista previa (renderiza la estructura genérica del backend)
function PreviewTabla({ preview, config }) {
  if (!preview) return <div className="p-8 text-center text-sm text-gray-400">Configurá el reporte para ver la vista previa</div>;
  const cols = preview.columnas || [];
  const agrupado = preview.agrupacion !== 'none';
  const money = new Set(preview.moneyKeys || []);
  const firstMoneyIdx = cols.findIndex(c => money.has(c.key));
  const rotColspan = firstMoneyIdx > 0 ? firstMoneyIdx : 1;
  const nCols = cols.length;

  const totalFilas = (preview.grupos || []).reduce((a, g) => a + g.filas.length, 0);
  if (!totalFilas) return <div className="p-8 text-center text-sm text-gray-400">No hay comprobantes con esos filtros</div>;

  const totRow = (label, tot, cls) => (
    <tr className={cls}>
      <td colSpan={rotColspan} className="px-2 py-1 font-semibold">{label}</td>
      {cols.slice(rotColspan).map(c => (
        <td key={c.key} className="px-2 py-1 text-right font-semibold">{money.has(c.key) ? fmtNum(tot[c.key]) : ''}</td>
      ))}
    </tr>
  );

  return (
    <table className="w-full text-xs">
      <thead className="sticky top-0">
        <tr className="bg-blue-50 text-gray-700">
          {cols.map(c => (
            <th key={c.key} className={`px-2 py-2 font-semibold border-b border-blue-200 whitespace-nowrap ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'}`}>{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(preview.grupos || []).map((g, gi) => (
          <>
            {agrupado && g.label && (
              <tr key={`h${gi}`} className="bg-blue-50/60"><td colSpan={nCols} className="px-2 py-1.5 font-bold text-gray-700">{g.label}</td></tr>
            )}
            {g.filas.map((f, fi) => (
              <tr key={`${gi}-${fi}`} className={`border-b border-gray-100 ${f.__neg ? 'text-red-600' : 'text-gray-700'}`}>
                {cols.map(c => (
                  <td key={c.key} className={`px-2 py-1 whitespace-nowrap ${money.has(c.key) || c.tipo === 'num' ? 'text-right' : c.align === 'center' ? 'text-center' : ''} ${c.key === 'consecutivo' || c.key === 'clave' ? 'font-mono text-[11px]' : ''}`}>
                    {money.has(c.key) || c.tipo === 'num' ? fmtNum(f[c.key]) : f[c.key]}
                  </td>
                ))}
              </tr>
            ))}
            {agrupado && g.label && preview.mostrarTotales && totRow(`Total ${g.label}`, g.tot, 'bg-gray-50 border-y border-gray-200')}
          </>
        ))}
        {preview.mostrarTotales && (preview.moneyKeys || []).length > 0 && totRow('TOTAL DEL PERÍODO', preview.gran, 'bg-blue-100 border-t-2 border-gray-700')}
      </tbody>
    </table>
  );
}
